'use client';

import { useEffect } from 'react';
import { AppShell, Box } from '@mantine/core';
import { useDisclosure, useMounted } from '@mantine/hooks';
import { useForegroundNotification } from '@/src/app/(b2c)/_hooks/use-foreground-notification';
import { useIsAuthenticated } from '@/src/modules/auth/hooks/use-is-authenticated';
import { useUserAddressSession } from '@/src/modules/auth/hooks/use-user-address-session';
import { useAddressStore } from '@/src/modules/auth/stores/use-address-store';
import { useAuthStore } from '@/src/modules/auth/stores/use-auth-store';
import { useSubscribeToTopics } from '@/src/modules/notification/hooks/use-subscribe-to-topics';
import { Footer } from '@/src/shared/components/footer';
import { Header } from '@/src/shared/components/header/header';
import {
  HEADER_HEIGHT,
  MIGRATION_BANNER_HEIGHT,
  UNAUTHORIZED_HEADER_HEIGHT,
} from '@/src/shared/constants/ui/header';
import { useFirebaseCloudMessagingToken } from '@/src/shared/hooks/use-firebase-cloud-messaging-token';
import { useGeolocation } from '@/src/shared/hooks/useGeolocation';
import { useLayoutHorizontalSpacing } from '../_hooks/use-layout-horizontal-spacing';

export default function Layout({ children }: React.PropsWithChildren) {
  useUserAddressSession();

  const isMounted = useMounted();
  const isAuthenticated = useIsAuthenticated();
  const layoutHorizontalSpacing = useLayoutHorizontalSpacing();
  const { locationInfo } = useGeolocation();
  const setCoordinate = useAddressStore((s) => s.setCoordinate);
  const authStatus = useAuthStore((s) => s.status);
  const [migrationBannerShown, { close: hideMigrationBanner }] =
    useDisclosure(true);
  const appShellHeaderHeight =
    (isAuthenticated ? HEADER_HEIGHT : UNAUTHORIZED_HEADER_HEIGHT) +
    (migrationBannerShown ? MIGRATION_BANNER_HEIGHT : 0);

  const { token: deviceToken } = useFirebaseCloudMessagingToken();
  useSubscribeToTopics({ deviceToken });
  useForegroundNotification();

  useEffect(() => {
    if (locationInfo && authStatus !== 'loading') {
      setCoordinate(
        locationInfo?.coords.latitude,
        locationInfo?.coords.longitude
      );
    }
  }, [locationInfo, authStatus]);

  if (!isMounted) {
    return null;
  }

  return (
    <AppShell header={{ height: appShellHeaderHeight }} className="bg-gray-50">
      <Header
        migrationBannerShown={migrationBannerShown}
        onHideMigrationBanner={hideMigrationBanner}
      />

      <AppShell.Main className="flex min-h-dvh w-full flex-col">
        <Box px={layoutHorizontalSpacing} py="xl" className="flex-1">
          {children}
        </Box>

        <Footer />
      </AppShell.Main>
    </AppShell>
  );
}
